/**
 * WordPress dependencies
 */
import { _x } from '@safe-wordpress/i18n';
import type {
	UnitControlProps,
	UnitControlOnChangeCallback,
} from '@safe-wordpress/components';

/**
 * Internal dependencies
 */
import {
	StyledUnitControl,
	ControlWrapper,
} from './styles/focal-point-picker-style';
import { fractionToPercentage } from './utils';
import type { FocalPointAxis, FocalPointPickerControlsProps } from './types';

const TEXTCONTROL_MIN = 0;
const TEXTCONTROL_MAX = 100;
const noop = () => {};

export default function FocalPointPickerControls( {
	__nextHasNoMarginBottom,
	hasHelpText,
	onChange = noop,
	point = {
		x: 0.5,
		y: 0.5,
	},
	disablePositionX,
	disablePositionY,
}: FocalPointPickerControlsProps ): JSX.Element {
	const valueX = fractionToPercentage( point.x );
	const valueY = fractionToPercentage( point.y );

	const handleChange = (
		value: Parameters< UnitControlOnChangeCallback >[ 0 ],
		axis: FocalPointAxis
	) => {
		if ( value === undefined ) {
			return;
		}

		const num = parseInt( value, 10 );

		if ( ! isNaN( num ) ) {
			onChange( { ...point, [ axis ]: num / 100 } );
		}
	};

	return (
		<ControlWrapper
			className="focal-point-picker__controls"
			__nextHasNoMarginBottom={ __nextHasNoMarginBottom }
			hasHelpText={ hasHelpText }
			gap={ 4 }
			disablePositionX={ disablePositionX }
			disablePositionY={ disablePositionY }
		>
			<FocalPointUnitControl
				label={ _x( 'Left', 'text', 'nelio-ab-testing' ) }
				aria-label={ _x(
					'Focal point left position',
					'text',
					'nelio-ab-testing'
				) }
				value={ [ valueX, '%' ].join( '' ) }
				isDisabled={ disablePositionX }
				onChange={ ( next ) => handleChange( next, 'x' ) }
				dragDirection="e"
			/>
			<FocalPointUnitControl
				label={ _x( 'Top', 'text', 'nelio-ab-testing' ) }
				aria-label={ _x(
					'Focal point top position',
					'text',
					'nelio-ab-testing'
				) }
				value={ [ valueY, '%' ].join( '' ) }
				isDisabled={ disablePositionY }
				onChange={ ( next ) => handleChange( next, 'y' ) }
				dragDirection="s"
			/>
		</ControlWrapper>
	);
}

function FocalPointUnitControl(
	props: UnitControlProps & { readonly isDisabled: boolean }
) {
	return (
		<StyledUnitControl
			__next40pxDefaultSize
			className="focal-point-picker__controls-position-unit-control"
			labelPosition="top"
			max={ TEXTCONTROL_MAX }
			min={ TEXTCONTROL_MIN }
			disabled={ props.isDisabled }
			units={ [ { value: '%', label: '%' } ] }
			{ ...props }
		/>
	);
}
